import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Search, RefreshCw, Eye, Send, CheckCircle2, XCircle, Filter, FileText } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatCard } from "../../components/dashboard/Shared";

const STATUS_STYLE = {
  sent: "text-[#00E676] bg-[#00E676]/10",
  failed: "text-[#FF3B30] bg-[#FF3B30]/10",
  skipped_prefs: "text-[#FFD600] bg-[#FFD600]/10",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
};

export default function EmailCenter() {
  const [tab, setTab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({});
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState("");
  const [templateFilter, setTemplateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);   // { subject, html, template }
  const [detail, setDetail] = useState(null);     // log detail
  const [testTo, setTestTo] = useState("");
  const [testTemplate, setTestTemplate] = useState("otp");
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLogs = () => {
    const params = { limit: 200 };
    if (status) params.status = status;
    if (templateFilter) params.template = templateFilter;
    if (search) params.search = search;
    api.get("/admin/email/logs", { params })
      .then(({ data }) => { setLogs(data.items || []); setCounts(data.counts || {}); })
      .catch(() => toast.error("Failed to load logs"));
  };
  const loadTemplates = () => api.get("/admin/email/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); loadTemplates(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [status, templateFilter, search]);

  const doPreview = async (key) => {
    try {
      const { data } = await api.get(`/admin/email/templates/${key}/preview`);
      setPreview(data);
    } catch { toast.error("Preview failed"); }
  };

  const doResend = async (logId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email/logs/${logId}/resend`);
      toast.success(data.ok ? "Email resent" : `Resend failed: ${data.error || ""}`);
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
    finally { setBusy(false); }
  };

  const doTestSend = async () => {
    if (!testTo) return toast.error("Enter a recipient email");
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email/test-send", { template: testTemplate, to: testTo });
      setTestResult(data);
      if (data.ok) toast.success(`Sent · id ${(data.message_id || "").slice(0, 8)}…`);
      else toast.error(`Failed: ${data.error || "unknown"}`);
      loadLogs();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Request failed" });
      toast.error("Test send failed");
    }
    finally { setBusy(false); }
  };

  const openDetail = async (log) => {
    try {
      const { data } = await api.get(`/admin/email/logs/${log.log_id}`);
      setDetail(data);
    } catch { toast.error("Could not load detail"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Email Center" sub="Inspect email delivery, resend failed messages, preview templates and run test sends" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={counts.total || 0} testId="kpi-total" />
        <StatCard label="Sent" value={counts.sent || 0} accent="text-[#00E676]" testId="kpi-sent" />
        <StatCard label="Failed" value={counts.failed || 0} accent="text-[#FF3B30]" testId="kpi-failed" />
        <StatCard label="Skipped (prefs)" value={counts.skipped_prefs || 0} accent="text-[#FFD600]" testId="kpi-skipped" />
      </div>

      <div className="flex gap-1 border-b border-white/10">
        <button data-testid="tab-logs" onClick={() => setTab("logs")}
                className={`px-4 py-2 text-sm ${tab === "logs" ? "text-white border-b-2 border-[#7000FF]" : "text-gray-400"}`}>
          Logs
        </button>
        <button data-testid="tab-templates" onClick={() => setTab("templates")}
                className={`px-4 py-2 text-sm ${tab === "templates" ? "text-white border-b-2 border-[#7000FF]" : "text-gray-400"}`}>
          Templates ({templates.length})
        </button>
        <button data-testid="tab-testsend" onClick={() => setTab("testsend")}
                className={`px-4 py-2 text-sm ${tab === "testsend" ? "text-white border-b-2 border-[#7000FF]" : "text-gray-400"}`}>
          Test Send
        </button>
      </div>

      {tab === "logs" && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search recipient / subject…" data-testid="email-search"
                      className="input-dark !pl-9 !w-72" /></div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="input-dark !w-40" data-testid="email-status-filter">
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped_prefs">Skipped</option>
            </select>
            <select value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}
                    className="input-dark !w-52" data-testid="email-template-filter">
              <option value="">All templates</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.key}</option>)}
            </select>
            <button onClick={loadLogs} className="btn-outline text-sm flex items-center gap-2" data-testid="email-refresh"><RefreshCw size={14} /> Refresh</button>
          </div>

          <div className="card-dark overflow-x-auto" data-testid="email-logs-table">
            {logs.length === 0 ? <p className="p-8 text-center text-gray-500 text-sm">No logs match filters.</p> : (
              <Table headers={["Sent", "Recipient", "Template", "Subject", "Status", "Error", ""]}>
                {logs.map((l) => (
                  <tr key={l.log_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <Td className="text-xs">{fmt(l.created_at)}</Td>
                    <Td className="text-white text-xs">{l.recipient}</Td>
                    <Td className="text-[#00F0FF] text-xs">{l.template}</Td>
                    <Td className="text-xs !whitespace-normal max-w-md">{l.subject}</Td>
                    <Td><span className={`text-[10px] px-2 py-0.5 rounded uppercase ${STATUS_STYLE[l.status] || ""}`}>{l.status}</span></Td>
                    <Td className="text-xs text-[#FF3B30] max-w-xs !whitespace-normal truncate" title={l.error}>{l.error ? l.error.slice(0, 60) + "…" : "—"}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button onClick={() => openDetail(l)} data-testid={`view-log-${l.log_id}`}
                                className="text-[#00F0FF] hover:text-white text-xs flex items-center gap-1">
                          <Eye size={12} />
                        </button>
                        <button disabled={busy} onClick={() => doResend(l.log_id)}
                                data-testid={`resend-log-${l.log_id}`}
                                className="text-[#7000FF] hover:text-white text-xs flex items-center gap-1">
                          <Send size={12} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}

      {tab === "templates" && (
        <div className="card-dark overflow-x-auto" data-testid="templates-table">
          <Table headers={["Key", "Category", "Subject", ""]}>
            {templates.map((t) => (
              <tr key={t.key} className="border-b border-white/5 hover:bg-white/[0.02]">
                <Td className="text-white text-xs">{t.key}</Td>
                <Td className="text-xs text-gray-500 uppercase">{t.category}</Td>
                <Td className="text-xs !whitespace-normal">{t.subject}</Td>
                <Td>
                  <button onClick={() => doPreview(t.key)} data-testid={`preview-${t.key}`}
                          className="text-[#00F0FF] hover:text-white text-xs flex items-center gap-1">
                    <Eye size={12} /> Preview
                  </button>
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      {tab === "testsend" && (
        <div className="card-dark p-6 max-w-lg" data-testid="testsend-panel">
          <p className="text-white text-sm mb-4">Send a rendered template with sample data to any address (for QA / template validation).</p>
          <label className="label-xs">Template</label>
          <select value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)}
                  className="input-dark mb-4" data-testid="testsend-template">
            {templates.map((t) => <option key={t.key} value={t.key}>{t.key}</option>)}
          </select>
          <label className="label-xs">Recipient</label>
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                  className="input-dark mb-4" placeholder="test@example.com" data-testid="testsend-to" />
          <button onClick={doTestSend} disabled={busy || !testTo}
                  data-testid="testsend-btn"
                  className="btn-primary w-full py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            <Send size={14} /> {busy ? "Sending…" : "Send Test Email"}
          </button>
          {testResult && (
            <div className={`mt-4 p-3 rounded-lg border text-xs ${testResult.ok ? "bg-[#00E676]/10 border-[#00E676]/30 text-[#00E676]" : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]"}`}
                  data-testid="testsend-result">
              {testResult.ok ? (
                <div className="space-y-1">
                  <p className="font-medium">✓ Sent successfully</p>
                  <p className="text-gray-400">Subject: <span className="text-white">{testResult.subject}</span></p>
                  <p className="text-gray-400">Resend Message ID: <span className="text-white tabular break-all">{testResult.message_id}</span></p>
                  <p className="text-gray-400">Log ID: <span className="text-white tabular">{testResult.log_id}</span></p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">✗ Failed</p>
                  <p className="text-gray-300">{testResult.error}</p>
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-3">Sending from <span className="text-white">ChartzFx Support &lt;support@chartzfx.com&gt;</span> (security emails from support@chartzfx.com).</p>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)} />
          <div className="relative card-dark w-full max-w-3xl h-[90vh] p-0 border border-white/10 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">{preview.template}</p>
                <p className="text-white text-sm">{preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-white text-xl">×</button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="log-detail-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative card-dark w-full max-w-3xl h-[90vh] p-0 border border-white/10 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <p className="text-xs text-gray-500">{detail.log_id} · {fmt(detail.created_at)}</p>
                <p className="text-white text-sm">{detail.subject}</p>
                <p className="text-xs text-gray-500 mt-1">To: {detail.recipient} · Template: {detail.template}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-white text-xl">×</button>
            </div>
            {detail.error && (
              <div className="bg-[#FF3B30]/10 border-b border-[#FF3B30]/30 text-[#FF3B30] text-xs px-4 py-2">Error: {detail.error}</div>
            )}
            <iframe title="detail" srcDoc={detail.html || "<p style='padding:20px;color:#666'>No HTML captured</p>"} className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
